import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type UseMetricChartResult, useMetricChart } from '@/components/charts/generic';
import type { SwimSpeedDataPoint, ThresholdRange } from '@/domain/behavior';
import { parseLocale } from '@/utils/localeFormatters';

import { buildSwimSpeedSpec } from '../../BehaviorSwimSpeed/swimSpeedChartSpec';

interface UseOverviewSwimSpeedChartDataParams {
    swimSpeedDataPoints: SwimSpeedDataPoint[];
    swimSpeedThreshold: ThresholdRange;
}

interface UseOverviewSwimSpeedChartDataReturn {
    chart: UseMetricChartResult<'line'>;
    showOnlyActiveSwimSpeed: boolean;
    setShowOnlyActiveSwimSpeed: (value: boolean) => void;
}

/**
 * The dashboard's swim speed chart: the active fish's speed by default, every fish's when the
 * switch is off.
 */
export function useOverviewSwimSpeedChartData({
    swimSpeedDataPoints,
    swimSpeedThreshold,
}: UseOverviewSwimSpeedChartDataParams): UseOverviewSwimSpeedChartDataReturn {
    const { t, i18n } = useTranslation('behaviorModule');
    const [showOnlyActiveSwimSpeed, setShowOnlyActiveSwimSpeed] = useState(true);

    const spec = useMemo(
        () =>
            buildSwimSpeedSpec({
                points: swimSpeedDataPoints,
                threshold: swimSpeedThreshold,
                t,
                locale: parseLocale(i18n.language),
                activeOnly: showOnlyActiveSwimSpeed,
            }),
        [swimSpeedDataPoints, swimSpeedThreshold, t, i18n.language, showOnlyActiveSwimSpeed]
    );

    return { chart: useMetricChart(spec), showOnlyActiveSwimSpeed, setShowOnlyActiveSwimSpeed };
}
